import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { logger } from '../utils/logger';
import { baseAxiosConfig, applyVerticalOverrideInterceptor } from '../utils/apiConfig';

const revenueOpportunityAxios = applyVerticalOverrideInterceptor(
  axios.create(baseAxiosConfig)
);

async function getAuthedUser() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    logger.error('No authenticated user found');
    throw new Error('Authentication required');
  }
  const token = await user.getIdToken();
  return { user, token };
}

function normalizeError(error, context) {
  logger.error(`Error in ${context}:`, error);
  if (error.response) {
    logger.error('Server response:', error.response.data);
    throw new Error(
      error.response.data?.message ||
        error.response.data?.detail ||
        'Server error'
    );
  }
  throw error;
}

/**
 * Run the Local Revenue Opportunity Model for the current user.
 *
 * @param {Object} payload
 * @param {Object} payload.inputs - Customer-provided facts (required + optional)
 * @param {string} [payload.customerName] - Name of the customer's own business
 * @param {string} [payload.customerMapsUri] - maps_uri of the customer's business
 * @param {Object} [payload.config] - Optional model configuration overrides
 * @param {boolean} [payload.save=true] - Persist the report for history
 * @returns {Promise<Object>} The full modeled report response
 */
export async function calculateRevenueOpportunity({
  inputs,
  customerName = null,
  customerMapsUri = null,
  config = null,
  save = true,
} = {}) {
  try {
    const { user, token } = await getAuthedUser();

    const body = {
      user: user.email,
      inputs,
      customer_name: customerName,
      customer_maps_uri: customerMapsUri,
      save,
    };
    if (config) {
      body.config = config;
    }

    const response = await revenueOpportunityAxios.post(
      '/revenue-opportunity/calculate',
      body,
      {
        params: { user: user.email },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.data) {
      throw new Error('No data received from server');
    }
    return response.data;
  } catch (error) {
    return normalizeError(error, 'calculateRevenueOpportunity');
  }
}

/**
 * Fetch the most recently saved Revenue Opportunity report.
 * @returns {Promise<Object>} The latest report response (success may be false).
 */
export async function getLatestRevenueOpportunity() {
  try {
    const { user, token } = await getAuthedUser();
    const response = await revenueOpportunityAxios.get(
      '/revenue-opportunity/latest',
      {
        params: { user: user.email },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getLatestRevenueOpportunity');
  }
}

/**
 * Fetch the user's Revenue Opportunity report history + trend series.
 * @returns {Promise<Object>} History response with reports[] and trend[].
 */
export async function getRevenueOpportunityHistory() {
  try {
    const { user, token } = await getAuthedUser();
    const response = await revenueOpportunityAxios.get(
      '/revenue-opportunity/history',
      {
        params: { user: user.email },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  } catch (error) {
    return normalizeError(error, 'getRevenueOpportunityHistory');
  }
}

const revenueOpportunityService = {
  calculateRevenueOpportunity,
  getLatestRevenueOpportunity,
  getRevenueOpportunityHistory,
};

export default revenueOpportunityService;
